/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_SESSION = 'fundle_abhinav_session_id';
const STORAGE_DISMISSED = 'fundle_abhinav_dismissed_at';
const STORAGE_OPENED_ONCE = 'fundle_abhinav_opened_once';
// Re-engage 6 hours after dismissal
const REENGAGE_MS = 6 * 60 * 60 * 1000;

const AbhinavAgent = () => {
  const location = useLocation();
  // Suppress the visitor-facing agent on admin / leads / privacy routes.
  const isSuppressedRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/leads');

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  // captured shown in UI as a debug-friendly summary (name/company/mobile)
  const [, setCaptured] = useState({ name: null, company: null, mobile: null });
  const [handoffUrl, setHandoffUrl] = useState(null);
  const [handoffOptions, setHandoffOptions] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Restore session id if visitor returns
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_SESSION);
    if (saved) setSessionId(saved);
  }, []);

  // Auto-open on first visit (3-second delay). Respect dismissed-cooldown.
  // Skip auto-open on mobile viewports so the widget does not obscure the
  // header/nav or hero CTAs on first paint.
  useEffect(() => {
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_DISMISSED) || '0', 10);
    const cooldownActive = dismissedAt && (Date.now() - dismissedAt) < REENGAGE_MS;
    if (cooldownActive) return;
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    const t = setTimeout(() => setIsOpen(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const persistSession = useCallback((id) => {
    if (id) {
      localStorage.setItem(STORAGE_SESSION, id);
      setSessionId(id);
    }
  }, []);

  const sendBootstrap = useCallback(async () => {
    if (bootstrapped) return;
    setBootstrapped(true);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/agent/chat`, {
        message: '',
        session_id: sessionId,
        bootstrap: true,
      });
      persistSession(res.data.session_id);
      setMessages([{ role: 'agent', text: res.data.reply }]);
      if (res.data.captured) setCaptured(res.data.captured);
      if (res.data.handoff_url) setHandoffUrl(res.data.handoff_url);
      if (res.data.handoff_options) setHandoffOptions(res.data.handoff_options);
    } catch (e) {
      setMessages([{
        role: 'agent',
        text: "Hey 👋 I'm Abhinav from Fundle. Are you exploring loyalty for a brand or a mall?",
      }]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, persistSession, bootstrapped]);

  // First time the widget opens → kick off the agent greeting
  useEffect(() => {
    if (isOpen && messages.length === 0 && !loading) {
      const openedOnce = localStorage.getItem(STORAGE_OPENED_ONCE);
      if (!openedOnce) {
        localStorage.setItem(STORAGE_OPENED_ONCE, '1');
      }
      sendBootstrap();
    }
  }, [isOpen, messages.length, loading, sendBootstrap]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when window opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const handleDismiss = () => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_DISMISSED, String(Date.now()));
  };

  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/agent/chat`, {
        message: text,
        session_id: sessionId,
      });
      persistSession(res.data.session_id);
      setMessages(prev => [...prev, { role: 'agent', text: res.data.reply }]);
      if (res.data.captured) setCaptured(res.data.captured);
      if (res.data.handoff_url) setHandoffUrl(res.data.handoff_url);
      if (res.data.handoff_options) setHandoffOptions(res.data.handoff_options);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'agent',
        text: "Connection blipped. Ping me directly: https://wa.me/919910530372",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickReplies = messages.length <= 1 ? [
    'Loyalty for a brand',
    'ADSR for a mall',
    'Book a demo',
  ] : null;

  if (isSuppressedRoute) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9997]" data-testid="abhinav-agent">
      {/* Chat Window */}
      {isOpen && (
        <div
          className="absolute bottom-20 right-0 w-[380px] sm:w-[400px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-[abhinav-pop_220ms_ease-out]"
          data-testid="abhinav-window"
          style={{ boxShadow: '0 24px 60px -12px rgba(5,10,31,0.45), 0 0 0 1px rgba(212,175,55,0.18)' }}
        >
          {/* Header */}
          <div className="px-5 py-4 bg-[#050A1F] flex items-center justify-between flex-shrink-0 border-b border-[#D4AF37]/20">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A07A1F] flex items-center justify-center text-[#050A1F] font-bold text-base shadow-md">
                  A
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#050A1F]" />
              </div>
              <div>
                <p className="text-white font-semibold text-[15px] leading-tight">Abhinav · Fundle.ai</p>
                <p className="text-[#D4AF37]/85 text-[11px] tracking-wide uppercase mt-0.5">Loyalty & ADSR Expert · Online</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/60 hover:text-white transition-colors p-1.5 rounded-full hover:bg-white/10"
              data-testid="abhinav-close"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[300px] max-h-[400px] bg-[#FBFAF6]">
            {messages.length === 0 && loading && (
              <div className="flex gap-2 justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <Loader2 size={16} className="text-[#D4AF37] animate-spin" />
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 text-[14px] leading-[1.5] ${
                  msg.role === 'user'
                    ? 'bg-[#050A1F] text-white rounded-2xl rounded-br-sm'
                    : 'bg-white text-[#050A1F] rounded-2xl rounded-bl-sm border border-slate-200 shadow-sm'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}

            {loading && messages.length > 0 && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Handoff CTAs — Abhinav primary, Anmol secondary */}
            {(handoffOptions || handoffUrl) && (
              <div className="pt-2 space-y-2" data-testid="abhinav-handoff-block">
                {(handoffOptions && handoffOptions.length
                  ? handoffOptions
                  : [{ name: 'Abhinav', url: handoffUrl }]
                ).map((opt, i) => (
                  <a
                    key={opt.name}
                    href={opt.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`abhinav-handoff-cta-${opt.name.toLowerCase()}`}
                    className={
                      i === 0
                        ? 'block w-full text-center bg-gradient-to-r from-[#D4AF37] to-[#B5912A] hover:from-[#E2BC44] hover:to-[#C09A2D] text-[#050A1F] font-bold py-3 px-4 rounded-xl shadow-lg transition-all hover:scale-[1.02] text-sm tracking-wide'
                        : 'block w-full text-center bg-white border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#050A1F] font-semibold py-2.5 px-4 rounded-xl transition-all text-sm'
                    }
                  >
                    {i === 0
                      ? `Continue on WhatsApp with ${opt.name} →`
                      : `Or reach ${opt.name} on WhatsApp`}
                  </a>
                ))}
                <p className="text-center text-[11px] text-slate-500 mt-1">Opens WhatsApp with your details pre-filled</p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick replies */}
          {quickReplies && !handoffUrl && !handoffOptions && (
            <div className="px-4 pb-2 pt-1 flex flex-wrap gap-1.5 bg-[#FBFAF6] border-t border-slate-100">
              {quickReplies.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q)}
                  disabled={loading}
                  data-testid={`abhinav-quick-reply-${i}`}
                  className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-[#D4AF37]/40 text-[#050A1F] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-slate-200 flex gap-2 flex-shrink-0 bg-white">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={handoffUrl ? 'Or keep chatting...' : 'Type your message...'}
              disabled={loading}
              className="flex-1 px-4 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-[14px] text-[#050A1F] placeholder-slate-400 focus:outline-none focus:border-[#D4AF37] focus:bg-white transition-colors disabled:opacity-60"
              data-testid="abhinav-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              data-testid="abhinav-send"
              className="w-10 h-10 rounded-full bg-[#050A1F] hover:bg-[#0E1740] disabled:opacity-40 disabled:hover:bg-[#050A1F] flex items-center justify-center transition-colors"
              aria-label="Send message"
            >
              {loading ? <Loader2 size={16} className="text-white animate-spin" /> : <Send size={16} className="text-white" />}
            </button>
          </div>

          {/* Footer */}
          <div className="px-4 pb-2.5 text-center text-[10px] text-slate-400 bg-white tracking-wide">
            Powered by Fundle AI · Replies in under 30 sec
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <div className="relative">
        {!isOpen && messages.length === 0 && (
          <div className="absolute bottom-16 right-0 bg-white rounded-2xl shadow-xl border border-slate-200 px-4 py-2.5 whitespace-nowrap animate-[abhinav-pulse_3s_ease-in-out_infinite]">
            <p className="text-[#050A1F] text-[13px] font-semibold leading-tight">Hi 👋 I&apos;m Abhinav</p>
            <p className="text-slate-500 text-[11px] mt-0.5">Got a loyalty or ADSR question?</p>
            <div className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white border-r border-b border-slate-200 transform rotate-45" />
          </div>
        )}

        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid="abhinav-toggle"
          aria-label={isOpen ? 'Close chat' : 'Chat with Abhinav'}
          className={`relative w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
            isOpen
              ? 'bg-white border-2 border-slate-200'
              : 'bg-gradient-to-br from-[#D4AF37] to-[#A07A1F] hover:scale-110'
          }`}
        >
          {isOpen ? (
            <X size={22} className="text-[#050A1F]" />
          ) : (
            <>
              <span className="text-[#050A1F] font-bold text-xl">A</span>
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white" />
            </>
          )}
        </button>
        {!isOpen && (
          <div className="absolute inset-0 w-16 h-16 rounded-full bg-[#D4AF37]/40 animate-ping pointer-events-none" style={{ animationDuration: '2.5s' }} />
        )}
      </div>

      <style>{`
        @keyframes abhinav-pop {
          0% { opacity: 0; transform: translateY(12px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes abhinav-pulse {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
};

export default AbhinavAgent;
